// app/frontend/pages/Admin/Categories/Index.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Table,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  Grid,
  Card,
  SegmentedControl,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
  IconGripVertical,
  IconLayoutGrid,
  IconLayoutList,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface Category {
  id: number;
  name: string;
  slug: string;
  position: number;
  video_count: number;
  created_at: string;
  updated_at: string;
}

interface Props {
  categories: Category[];
  stats: {
    total: number;
    with_videos: number;
    empty: number;
  };
}

export default function CategoriesIndex({ categories, stats }: Props) {
  const [deleteModal, { open: openDelete, close: closeDelete }] =
    useDisclosure(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );

  const isDesktop = useMediaQuery("(max-width: 1024px)");
  const [viewMode, setViewMode] = useState<string>(
    isDesktop ? "cards" : "table"
  );

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(`/admin/categories/${categoryToDelete.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        l2kNotify.success(
          "Deleted",
          `${categoryToDelete.name} has been deleted`
        );
        closeDelete();
        router.reload();
      } else {
        l2kNotify.error("Error", data.error || "Failed to delete category");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to delete category");
    }
  };

  const confirmDelete = (category: Category) => {
    setCategoryToDelete(category);
    openDelete();
  };

  // Category Card Component
  const CategoryCard = ({ category }: { category: Category }) => (
    <Card
      padding="md"
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Box>
            <L2KText size="sm" fw={600}>
              {category.name}
            </L2KText>
            <L2KCaption variant="tertiary">{category.slug}</L2KCaption>
          </Box>
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown
              styles={{
                dropdown: {
                  backgroundColor: "rgba(23, 23, 23, 0.95)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                component={Link}
                href={`/admin/categories/${category.id}/edit`}
              >
                Edit
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => confirmDelete(category)}
                disabled={category.video_count > 0}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Badge size="xs" variant="light" color="gray">
              #{category.position}
            </Badge>
            <Badge
              size="xs"
              color={category.video_count > 0 ? "blue" : "gray"}
              variant="light"
            >
              {category.video_count} videos
            </Badge>
          </Group>
          <L2KCaption variant="tertiary" size="xs">
            {category.updated_at}
          </L2KCaption>
        </Group>
      </Stack>
    </Card>
  );

  return (
    <>
      <SEO title="Categories - Admin - Learntokiz" />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Box>
            <L2KH2 mb="xs">Categories</L2KH2>
            <L2KText variant="secondary">
              {stats.total} total • {stats.with_videos} with videos •{" "}
              {stats.empty} empty
            </L2KText>
          </Box>
          <Group>
            <Link href="/admin/categories/new">
              <L2KButton leftSection={<IconPlus size={16} />}>
                Add Category
              </L2KButton>
            </Link>
          </Group>
        </Group>

        {/* View Toggle */}
        <Group justify="flex-end">
          <SegmentedControl
            value={viewMode}
            onChange={setViewMode}
            data={[
              {
                value: "table",
                label: (
                  <Group gap={4}>
                    <IconLayoutList size={16} />
                    <span style={{ display: isDesktop ? "none" : "inline" }}>
                      Table
                    </span>
                  </Group>
                ),
              },
              {
                value: "cards",
                label: (
                  <Group gap={4}>
                    <IconLayoutGrid size={16} />
                    <span style={{ display: isDesktop ? "none" : "inline" }}>
                      Cards
                    </span>
                  </Group>
                ),
              },
            ]}
            styles={{
              root: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 152, 0, 0.15)",
              },
              label: {
                color: "#94a3b8",
                fontWeight: 500,
              },
              indicator: {
                backgroundColor: "rgba(255, 152, 0, 0.2)",
                border: "1px solid rgba(255, 152, 0, 0.4)",
              },
            }}
          />
        </Group>

        {/* Table View */}
        {viewMode === "table" && (
          <Box style={{ overflowX: "auto" }}>
            <Table
              horizontalSpacing="md"
              verticalSpacing="sm"
              styles={{
                table: { backgroundColor: "transparent" },
                th: {
                  color: "#888",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  fontFamily: "'Public Sans', sans-serif",
                },
                td: { borderColor: "rgba(255, 255, 255, 0.05)" },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 50 }}>Order</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Videos</Table.Th>
                  <Table.Th>Updated</Table.Th>
                  <Table.Th style={{ width: 60 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {categories.map((category) => (
                  <Table.Tr key={category.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconGripVertical
                          size={16}
                          style={{ color: "#666", cursor: "grab" }}
                        />
                        <L2KCaption variant="tertiary">
                          {category.position}
                        </L2KCaption>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <L2KText size="sm" fw={500}>
                        {category.name}
                      </L2KText>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">
                        {category.slug}
                      </L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={category.video_count > 0 ? "blue" : "gray"}
                        variant="light"
                      >
                        {category.video_count} videos
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">
                        {category.updated_at}
                      </L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown
                          styles={{
                            dropdown: {
                              backgroundColor: "rgba(23, 23, 23, 0.95)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            },
                          }}
                        >
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            component={Link}
                            href={`/admin/categories/${category.id}/edit`}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => confirmDelete(category)}
                            disabled={category.video_count > 0}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {/* Card View */}
        {viewMode === "cards" && (
          <Grid>
            {categories.map((category) => (
              <Grid.Col key={category.id} span={{ base: 12, xs: 6, sm: 4 }}>
                <CategoryCard category={category} />
              </Grid.Col>
            ))}
          </Grid>
        )}

        {categories.length === 0 && (
          <Box ta="center" py="xl">
            <L2KText variant="tertiary">No categories yet</L2KText>
          </Box>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModal}
          onClose={closeDelete}
          title="Delete Category"
          styles={{
            header: { backgroundColor: "#171717" },
            body: { backgroundColor: "#171717" },
            title: { color: "#F0F0F0", fontFamily: "'Public Sans', sans-serif" },
          }}
        >
          <Stack gap="md">
            <L2KText>
              Are you sure you want to delete{" "}
              <strong>{categoryToDelete?.name}</strong>?
            </L2KText>
            <L2KText variant="secondary" size="sm">
              This action cannot be undone.
            </L2KText>
            <Group justify="flex-end" mt="md">
              <L2KButton variant="ghost" onClick={closeDelete}>
                Cancel
              </L2KButton>
              <L2KButton
                variant="outline"
                onClick={handleDelete}
                style={{ borderColor: "#ef4444", color: "#ef4444" }}
              >
                Delete
              </L2KButton>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </>
  );
}

CategoriesIndex.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
);
